import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LeadListItemDto, LeadStatus } from '@repo/shared-types';
import { apiFetch } from '../lib/api';

interface LeadsPage {
  items: LeadListItemDto[];
  nextCursor: string | null;
}

/** The filter the Leads screen's segmented control offers — `'all'` means no server-side
 * filter (`leadsQuerySchema`'s own default), so it's not itself a `LeadStatus`. */
export type LeadsFilter = LeadStatus | 'all';

/**
 * `GET /api/bots/:botId/leads?status=` — keyset-paginated via `useInfiniteQuery` (spec D4/M5
 * task-3), same shape as `useConversations`: manual "Load more" (`fetchNextPage`) rather
 * than infinite scroll, so React Query's own page cache does the accumulation. `status` is
 * part of the query key (triage-state design 2026-08-21 §C) so each filter tab gets its own
 * cache entry/pagination cursor rather than sharing one with the others.
 *
 * `options.enabled` (fix round 1, item 2) — `BotLeads.tsx` gates this on its own Overview
 * query having settled, so a cold load fetches the DEFAULT filter's status once (`new` when
 * there's something new, else `all`) instead of firing an `all` request first and then a
 * second `new` request the instant Overview resolves. Combined with (not overriding) the
 * existing `!!botId` guard.
 */
export function useLeads(botId: string | undefined, status: LeadsFilter = 'all', options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: ['bots', botId, 'leads', status],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('status', status);
      if (pageParam) params.set('cursor', pageParam);
      return apiFetch<LeadsPage>(`/api/bots/${botId}/leads?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!botId && (options?.enabled ?? true),
  });
}

/** Both mutations below share the same invalidation: every `leads` filter's cache entry (a
 * status change can move a row out of the tab it's currently shown in), `overview` (the rail's
 * `newLeads` pill counts `status: 'new'`), and `bots` (the switcher/All-bots health pills read
 * the same aggregate) — same triple `useSetUnansweredStatus` invalidates, for the same reason. */
function invalidateAfterLeadStatusChange(queryClient: ReturnType<typeof useQueryClient>, botId: string | undefined) {
  void queryClient.invalidateQueries({ queryKey: ['bots', botId, 'leads'] });
  void queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] });
  void queryClient.invalidateQueries({ queryKey: ['bots'] });
}

/** `PATCH /api/bots/:botId/leads/:leadId { status }` — the row actions (Mark contacted /
 * Archive / Mark new). Any status is reachable from any other by hand. */
export function useSetLeadStatus(botId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { leadId: string; status: LeadStatus }) =>
      apiFetch<LeadListItemDto>(`/api/bots/${botId}/leads/${input.leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: input.status }),
      }),
    onSuccess: () => invalidateAfterLeadStatusChange(queryClient, botId),
  });
}

/** `POST /api/bots/:botId/leads/bulk-status { ids, status }` — the bulk bar's "Mark contacted"
 * / "Archive". `{ updated }` counts CHANGED rows only (ids already at the target status don't
 * count) — the caller's toast must read "N updated", never "N of M" (task-5-brief). */
export function useBulkSetLeadStatus(botId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; status: LeadStatus }) =>
      apiFetch<{ updated: number }>(`/api/bots/${botId}/leads/bulk-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidateAfterLeadStatusChange(queryClient, botId),
  });
}
